// src/hr/pages/HrPayouts.js
import React, { useEffect, useMemo, useState } from "react";
import {
  collection, getDocs, addDoc, updateDoc, doc, query, orderBy, serverTimestamp, onSnapshot, where, getDoc
} from "firebase/firestore";
import { db } from "../../firebase";
import { useEmployeeAuth } from "../auth/EmployeeAuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { FaSearch, FaRupeeSign, FaHistory, FaMoneyBillWave, FaDownload, FaCheck, FaTimes } from "react-icons/fa";
import Papa from "papaparse";
import { useNavigate } from "react-router-dom";

const HrPayouts = () => {
  const { user: hr } = useEmployeeAuth();
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showPayModal, setShowPayModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showSalaryModal, setShowSalaryModal] = useState(false);
  const [editSalaryStructure, setEditSalaryStructure] = useState({});
    const [currentMonth, setCurrentMonth] = useState(new Date().getMonth()); // 0-11
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  const [payForm, setPayForm] = useState({
    baseSalary: 0,
    bonus: 0,
    deduction: 0,
    finalPaid: 0,
  });

  // REALTIME: Load employees + payouts
  useEffect(() => {
    const unsub1 = onSnapshot(collection(db, "employees"), snap => {
      setEmployees(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    const unsub2 = onSnapshot(
      query(collection(db, "payouts"), orderBy("paidOn", "desc")),
      snap => {
        setPayouts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      }
    );
    return () => { unsub1(); unsub2(); };
  }, []);

  // Compute employee summary
  const employeeSummary = useMemo(() => {
    return employees.map(emp => {
      const empPayouts = payouts.filter(p => p.employeeEmail === emp.email);
      const totalPaid = empPayouts.reduce((sum, p) => sum + (p.finalPaid || 0), 0);
      const lastPayout = empPayouts[0];
      const lastPaid = lastPayout?.paidOn?.seconds
        ? new Date(lastPayout.paidOn.seconds * 1000).toLocaleDateString()
        : "Never";
      const status = lastPayout ? "paid" : "pending";
      const grossSalary = emp.salaryStructure?.gross || 15000;
      const selectedMonthStr = new Date(currentYear, currentMonth).toLocaleString("default", {
        month: "long",
        year: "numeric"
      });

      return {
        id: emp.email,                    // ← THIS IS THE KEY FIX
          email: emp.email,
          name: emp.name,
          designation: emp.designation,
          profilePic: emp.profilePic,
          salary: grossSalary,              // ← Now correct
          salaryStructure: emp.salaryStructure,
          paidThisMonth: empPayouts.some(p => p.month === selectedMonthStr),
          totalPaid,
          lastPaid,
          status: empPayouts.some(p => p.month === selectedMonthStr) ? "paid" : "pending",
      };
    });
  }, [employees, payouts, currentMonth, currentYear]);

  // Filters
  const filtered = employeeSummary.filter(emp => {
    const q = search.toLowerCase();
    const matches = !q ||
      emp.name?.toLowerCase().includes(q) ||
      emp.email?.toLowerCase().includes(q);
    const statusOk = !statusFilter || emp.status === statusFilter;
    return matches && statusOk;
  });

  // Export CSV
  const handleExport = () => {
    const data = filtered.map(e => ({
      name: e.name,
      email: e.email,
      salary: e.salary,
      totalPaid: e.totalPaid,
      lastPaid: e.lastPaid,
      status: e.status,
    }));
    const csv = Papa.unparse(data);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `payouts_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  };

  // Open Pay Modal

const openPay = async (emp) => {
  setSelected(emp);

  // 🔥 FORCE FETCH latest employee doc
  const freshSnap = await getDoc(doc(db, "employees", emp.id));
  const freshData = freshSnap.data();

  console.log("FRESH DATA:", freshData);

  const summary = freshData?.leaveSummary?.[String(currentYear)] || {};
  const monthKey = String(currentMonth + 1);

  const deductedDays = summary[monthKey]?.days || 0;
  const deductionAmount = summary[monthKey]?.amount || 0;

  const autoDeduction =
    deductedDays > 0
      ? [
          {
            name: `Leave Deduction (${deductedDays} day${deductedDays > 1 ? "s" : ""})`,
            amount: deductionAmount,
          },
        ]
      : [];

  setPayForm({
    baseSalary: emp.salary || 15000,
    additions: [],
    deductions: autoDeduction,
    remarks: "",
  });

  setShowPayModal(true);
};

  // Submit Payout
    const submitPayout = async () => {
      const additionsTotal = (payForm.additions || []).reduce((a, i) => a + i.amount, 0);
      const deductionsTotal = (payForm.deductions || []).reduce((a, i) => a + i.amount, 0);
      const final = payForm.baseSalary + additionsTotal - deductionsTotal;

      try {
        await addDoc(collection(db, "payouts"), {
          employeeEmail: selected.email,
          employeeName: selected.name,
          baseSalary: payForm.baseSalary,
          additions: payForm.additions.filter(a => a.name && a.amount > 0),
          deductions: payForm.deductions.filter(d => d.name && d.amount > 0),
          remarks: payForm.remarks?.trim() || null,
          finalPaid: final,
          paidOn: serverTimestamp(),
          month: new Date(currentYear, currentMonth).toLocaleString("default", {
            month: "long",
            year: "numeric"
          }),
          status: "paid",
          paidBy: hr?.email,
        });

        alert(`₹${final.toLocaleString()} paid to ${selected.name}`);
        setShowPayModal(false);
      } catch (err) {
        alert("Payout failed: " + err.message);
      }
    };
  const history = payouts.filter(p => p.employeeEmail === selected?.email);

  // Totals
  const totalPayroll = employeeSummary.reduce((a, e) => a + e.salary, 0);
  const totalDisbursed = employeeSummary.reduce((a, e) => a + e.totalPaid, 0);
  const pendingCount = employeeSummary.filter(e => e.status === "pending").length;

  return (
    <div className="min-h-screen overflow-hidden bg-gradient-to-br from-neutral-950 via-purple-950 to-indigo-950">

      {/* HERO */}
      <div className="pt-16 pb-6 px-8 text-center">
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-7xl font-black bg-gradient-to-r from-white via-white/90 to-white/70 bg-clip-text text-transparent"
        >
          Payouts
        </motion.h1>
        <p className="text-white/60 text-xl mt-4">Pay salaries. Track CTC. Reward excellence.</p>
      </div>

      {/* SUMMARY CARDS */}
      <div className="max-w-7xl mx-auto px-8 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[
            { label: "Total Employees", value: employees.length, icon: "👥" },
            { label: "Monthly Payroll", value: `₹${totalPayroll.toLocaleString()}`, icon: "💸" },
            { label: "Total Disbursed", value: `₹${totalDisbursed.toLocaleString()}`, icon: "🏦" },
            { label: "Pending Payouts", value: pendingCount, icon: "⏳", color: "text-red-400" },
          ].map((card, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="backdrop-blur-3xl bg-white/10 border border-white/20 rounded-3xl p-8 text-center hover:bg-white/15 transition"
            >
              <div className="text-5xl mb-3">{card.icon}</div>
              <p className="text-white/60 text-sm">{card.label}</p>
              <p className={`text-3xl font-bold text-white ${card.color || ""}`}>{card.value}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* FILTER BAR */}
      <div className="max-w-7xl mx-auto px-8 mb-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="backdrop-blur-3xl bg-white/10 border border-white/20 rounded-3xl p-6 flex flex-wrap gap-4 items-center justify-between"
        >
          <div className="flex items-center gap-4 flex-1">
            <div className="relative">
              <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search employee..."
                className="pl-12 pr-6 py-4 bg-white/10 border border-white/20 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-cyan-400 w-80"
              />
            </div>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-6 py-4 bg-white/10 border border-white/20 rounded-2xl text-white">
              <option value="">All Status</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
            </select>
          </div>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleExport}
            className="px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl text-white font-bold shadow-xl hover:shadow-emerald-500/50 flex items-center gap-3"
          >
            <FaDownload /> Export
          </motion.button>
        </motion.div>
      </div>

      {/* EMPLOYEE GRID */}
      {/* EMPLOYEE LIST */}
            {/* EMPLOYEE LIST WITH MONTH NAVIGATION */}
            <div className="max-w-7xl mx-auto px-8">
              {/* MONTH NAVIGATION */}
              <div className="flex items-center justify-between mb-6">
                <button
                  onClick={() => {
                    const newDate = new Date(currentYear, currentMonth - 1);
                    setCurrentMonth(newDate.getMonth());
                    setCurrentYear(newDate.getFullYear());
                  }}
                  className="p-3 bg-white/10 hover:bg-white/20 rounded-xl transition"
                >
                  ← Previous
                </button>

                <h2 className="text-3xl font-bold text-white bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                  {new Date(currentYear, currentMonth).toLocaleString("default", {
                    month: "long",
                    year: "numeric",
                  })}
                </h2>

                <button
                  onClick={() => {
                    const newDate = new Date(currentYear, currentMonth + 1);
                    setCurrentMonth(newDate.getMonth());
                    setCurrentYear(newDate.getFullYear());
                  }}
                  className="p-3 bg-white/10 hover:bg-white/20 rounded-xl transition"
                >
                  Next →
                </button>
              </div>

              <div className="backdrop-blur-3xl bg-white/10 border border-white/20 rounded-3xl overflow-hidden">
                <table className="w-full">
            <thead>
              <tr className="bg-gradient-to-r from-cyan-500/20 to-blue-500/20">
                <th className="p-6 text-left text-white/80">Employee</th>
                <th className="p-6 text-left text-white/80">Salary</th>
                <th className="p-6 text-left text-white/80">Total Paid</th>
                <th className="p-6 text-left text-white/80">Last Paid</th>
                <th className="p-6 text-left text-white/80">Status</th>
                <th className="p-6 text-center text-white/80">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="p-20 text-center text-white/50">Loading payroll...</td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-20 text-center text-white/50">No employees found</td>
                </tr>
              ) : filtered.map((emp, i) => (
                <motion.tr
                  key={emp.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.03 }}
                  className="border-t border-white/10 hover:bg-white/5 transition"
                >
                  <td className="p-6">
                    <div 
                      className="flex items-center gap-4 cursor-pointer hover:text-cyan-400 transition"
                      onClick={() => navigate(`/employee/payroll/${emp.email}`)}
                    >
                      <img
                        src={emp.profilePic || `https://ui-avatars.com/api/?name=${emp.name}&background=0f172a&color=06b6d4`}
                        alt=""
                        className="w-12 h-12 rounded-full ring-2 ring-cyan-400/50"
                      />
                      <div>
                        <p className="text-white font-bold">{emp.name}</p>
                        <p className="text-cyan-300 text-sm">{emp.designation || "Employee"}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-6 text-white font-bold">
                    <button
                      onClick={() => {
                        setSelected(emp);
                        setEditSalaryStructure({
                          basic: emp.salaryStructure?.basic || 0,
                          hra: emp.salaryStructure?.hra || 0,
                          da: emp.salaryStructure?.da || 0,
                          cca: emp.salaryStructure?.cca || 0,
                          specialAllowance: emp.salaryStructure?.specialAllowance || 0,
                          performancePay: emp.salaryStructure?.performancePay || 0,
                          meal: emp.salaryStructure?.meal || 0,
                          gross: emp.salaryStructure?.gross || 15000,
                        });
                        setShowSalaryModal(true);
                      }}
                      className="hover:text-cyan-300 transition underline decoration-dotted"
                    >
                      ₹{emp.salary?.toLocaleString() || "0"}
                    </button>
                  </td>
                  <td className="p-6 text-emerald-300 font-bold">₹{emp.totalPaid.toLocaleString()}</td>
                  <td className="p-6 text-white/80">{emp.lastPaid}</td>
                  <td className="p-6">
                    <span className={`px-4 py-2 rounded-full text-xs font-bold text-white ${
                      emp.paidThisMonth
                        ? "bg-emerald-500/30"
                        : "bg-orange-500/30"
                    }`}>
                      {emp.paidThisMonth ? "Paid" : "Pending"} this month
                    </span>
                  </td>
                  <td className="p-6 text-center">
                    <div className="flex items-center justify-center gap-3">
                      <button
                        onClick={() => openPay(emp)}
                        className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-xl text-white font-bold hover:scale-105 transition shadow-lg"
                      >
                        Pay Now
                      </button>
                      <button
                        onClick={() => { setSelected(emp); setShowHistoryModal(true); }}
                        className="p-3 bg-white/10 hover:bg-white/20 rounded-xl"
                      >
                        <FaHistory className="text-purple-300" />
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* PAY MODAL */}
            {/* DYNAMIC PAYOUT MODAL — UNLIMITED ADDITIONS & DEDUCTIONS */}
            <AnimatePresence>
              {showPayModal && selected && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black/80 backdrop-blur-xl z-50 flex items-center justify-center p-4 overflow-y-auto"
                  onClick={() => setShowPayModal(false)}
                >
                  <motion.div
                    initial={{ scale: 0.9, y: 20 }}
                    animate={{ scale: 1, y: 0 }}
                    className="bg-white/10 backdrop-blur-3xl border border-white/20 rounded-3xl p-10 w-full max-w-4xl max-h-screen overflow-y-auto"
                    onClick={e => e.stopPropagation()}
                  >
                    <h2 className="text-4xl font-bold text-white mb-8 text-center">
                      Payout for {selected.name}
                    </h2>
                    <p className="text-center text-cyan-300 text-lg mb-8">
                      {new Date(currentYear, currentMonth).toLocaleString("default", {
                        month: "long",
                        year: "numeric"
                      })}
                    </p>

                    {/* Base Salary - Editable */}
                    <div className="mb-8 p-6 bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-2xl">
                      <label className="text-white/80 text-lg">Base Salary (Gross)</label>
                      <input
                        type="number"
                        value={payForm.baseSalary}
                        onChange={e => setPayForm(f => ({ ...f, baseSalary: Number(e.target.value) || 0 }))}
                        className="w-full mt-3 px-6 py-5 text-2xl font-bold text-white bg-white/10 border border-white/20 rounded-2xl focus:ring-4 focus:ring-cyan-400"
                      />
                    </div>

                    {/* Dynamic Additions */}
                    <div className="mb-6">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-xl font-bold text-emerald-300">Additions</h3>
                        <button
                          type="button"
onClick={() => setPayForm(f => ({ ...f, additions: [...(f.additions ?? []), { name: "", amount: 0 }] }))}                          className="px-4 py-2 bg-emerald-600/30 hover:bg-emerald-600/50 rounded-xl text-emerald-300 text-sm font-bold"
                        >
                          + Add Item
                        </button>
                      </div>
                      {payForm.additions?.map((item, i) => (
                        <div key={i} className="flex gap-3 mb-3">
                          <input
                            placeholder="e.g. Joining Bonus, Diwali Bonus"
                            value={item.name}
                            onChange={e => {
                              const newAdds = [...payForm.additions];
                              newAdds[i].name = e.target.value;
                              setPayForm(f => ({ ...f, additions: newAdds }));
                            }}
                            className="flex-1 px-5 py-4 bg-white/10 border border-white/20 rounded-xl text-white"
                          />
                          <input
                            type="number"
                            placeholder="Amount"
                            value={item.amount}
                            onChange={e => {
                              const newAdds = [...payForm.additions];
                              newAdds[i].amount = Number(e.target.value) || 0;
                              setPayForm(f => ({ ...f, additions: newAdds }));
                            }}
                            className="w-40 px-5 py-4 bg-white/10 border border-white/20 rounded-xl text-white"
                          />
                          <button
                            type="button"
                            onClick={() => setPayForm(f => ({ ...f, additions: f.additions.filter((_, idx) => idx !== i) }))}
                            className="px-4 text-red-400 hover:text-red-300"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>

                    {/* Dynamic Deductions */}
                    <div className="mb-8">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-xl font-bold text-red-300">Deductions</h3>
                        <button
                          type="button"
onClick={() => setPayForm(f => ({ ...f, deductions: [...(f.deductions ?? []), { name: "", amount: 0 }] }))}                          className="px-4 py-2 bg-red-600/30 hover:bg-red-600/50 rounded-xl text-red-300 text-sm font-bold"
                        >
                          + Add Item
                        </button>
                      </div>
                      {payForm.deductions?.map((item, i) => (
                        <div key={i} className="flex gap-3 mb-3">
                          <input
                            placeholder="e.g. Late Penalty, Loan Recovery"
                            value={item.name}
                            onChange={e => {
                              const newDeds = [...payForm.deductions];
                              newDeds[i].name = e.target.value;
                              setPayForm(f => ({ ...f, deductions: newDeds }));
                            }}
                            className="flex-1 px-5 py-4 bg-white/10 border border-white/20 rounded-xl text-white"
                          />
                          <input
                            type="number"
                            placeholder="Amount"
                            value={item.amount}
                            onChange={e => {
                              const newDeds = [...payForm.deductions];
                              newDeds[i].amount = Number(e.target.value) || 0;
                              setPayForm(f => ({ ...f, deductions: newDeds }));
                            }}
                            className="w-40 px-5 py-4 bg-white/10 border border-white/20 rounded-xl text-white"
                          />
                          <button
                            type="button"
                            onClick={() => setPayForm(f => ({ ...f, deductions: f.deductions.filter((_, idx) => idx !== i) }))}
                            className="px-4 text-red-400 hover:text-red-300"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>

                    {/* Remarks */}
                    <div className="mb-8">
                      <label className="text-white/80 text-lg">Remarks (Optional)</label>
                      <textarea
                        placeholder="e.g. Diwali bonus paid, Advance salary adjustment, etc."
                        value={payForm.remarks || ""}
                        onChange={e => setPayForm(f => ({ ...f, remarks: e.target.value }))}
                        className="w-full mt-3 px-6 py-4 bg-white/10 border border-white/20 rounded-2xl text-white placeholder-white/50 h-28 resize-none"
                      />
                    </div>

                    {/* Final Amount */}
                    <div className="p-8 bg-gradient-to-r from-cyan-600/30 to-blue-600/30 rounded-3xl text-center">
                      <p className="text-white/80 text-xl mb-3">Final Payout Amount</p>
                      <p className="text-6xl font-black text-white">
                        ₹{(
                          payForm.baseSalary +
                          (payForm.additions || []).reduce((a, i) => a + i.amount, 0) -
                          (payForm.deductions || []).reduce((a, i) => a + i.amount, 0)
                        ).toLocaleString()}
                      </p>
                    </div>

                    <div className="flex gap-6 mt-10">
                      <button
                        type="button"
                        onClick={() => setShowPayModal(false)}
                        className="flex-1 py-5 bg-white/10 hover:bg-white/20 rounded-2xl text-white font-bold text-xl"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        onClick={submitPayout}
                        className="flex-1 py-5 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl text-white font-bold text-xl shadow-xl hover:shadow-emerald-500/70"
                      >
                        Confirm & Pay Now
                      </button>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

      {/* HISTORY MODAL */}
      <AnimatePresence>
        {showHistoryModal && selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-xl z-50 flex items-center justify-center p-4"
            onClick={() => setShowHistoryModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              className="bg-white/10 backdrop-blur-3xl border border-white/20 rounded-3xl p-10 w-full max-w-4xl"
              onClick={e => e.stopPropagation()}
            >
              <h3 className="text-2xl font-bold text-white mb-6">Payout History — {selected.name}</h3>
              <div className="bg-white/5 rounded-2xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-white/10">
                      <th className="p-4 text-left text-white/80">Date</th>
                      <th className="p-4 text-left text-white/80">Month</th>
                      <th className="p-4 text-left text-white/80">Bonus</th>
                      <th className="p-4 text-left text-white/80">Deduction</th>
                      <th className="p-4 text-left text-white/80">Paid</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.length === 0 ? (
                      <tr><td colSpan={5} className="p-8 text-center text-white/50">No payouts yet</td></tr>
                    ) : history.map((p, i) => (
                      <tr key={i} className="border-t border-white/10">
                        <td className="p-4 text-white">
                          {p.paidOn?.seconds ? new Date(p.paidOn.seconds * 1000).toLocaleDateString() : "—"}
                        </td>
                        <td className="p-4 text-cyan-300">{p.month}</td>
                        <td className="p-4 text-emerald-300">₹{p.bonus}</td>
                        <td className="p-4 text-red-300">-₹{p.deduction}</td>
                        <td className="p-4 text-white font-bold">₹{p.finalPaid.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-8 text-right">
                <button onClick={() => setShowHistoryModal(false)} className="px-8 py-4 bg-white/10 hover:bg-white/20 rounded-2xl text-white font-bold">
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* SALARY BREAKDOWN & EDIT MODAL */}
      <AnimatePresence>
        {showSalaryModal && selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-xl z-50 flex items-center justify-center p-4"
            onClick={() => setShowSalaryModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              className="bg-white/10 backdrop-blur-3xl border border-white/20 rounded-3xl p-10 w-full max-w-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-8">
                <h2 className="text-xl font-bold text-white">
                  Salary Structure — {selected.name}
                </h2>
                <button
                  onClick={() => setShowSalaryModal(false)}
                  className="text-white/60 hover:text-white text-3xl"
                >
                  ×
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: "Basic", key: "basic" },
                  { label: "HRA", key: "hra" },
                  { label: "Dearness Allowance (DA)", key: "da" },
                  { label: "CCA", key: "cca" },
                  { label: "Special Allowance", key: "specialAllowance" },
                  { label: "Performance Pay", key: "performancePay" },
                  { label: "Meal Allowance", key: "meal" },
                ].map(({ label, key }) => (
                  <div key={key}>
                    <label className="text-white/80 text-sm">{label}</label>
                    <input
                      type="number"
                      value={editSalaryStructure[key] || 0}
                      onChange={(e) => {
                        const val = Number(e.target.value) || 0;
                        setEditSalaryStructure(prev => {
                          const updated = { ...prev, [key]: val };
                          // Auto-calculate gross
                          updated.gross =
                            updated.basic +
                            updated.hra +
                            updated.da +
                            updated.cca +
                            updated.specialAllowance +
                            updated.performancePay +
                            updated.meal;
                          return updated;
                        });
                      }}
                      className="w-full mt-2 px-6 py-4 bg-white/10 border border border-white/20 rounded-2xl text-white focus:ring-2 focus:ring-cyan-400"
                    />
                  </div>
                ))}
              </div>

              <div className="mt-8 p-6 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-2xl">
                <p className="text-white/80 text-m">Gross Monthly Salary</p>
                <p className="text-2xl font-black text-white mt-2">
                  ₹{(editSalaryStructure.gross || 0).toLocaleString()}
                </p>
              </div>

              <div className="flex justify-end gap-4 mt-8">
                <button
                  onClick={() => setShowSalaryModal(false)}
                  className="px-8 py-4 bg-white/10 hover:bg-white/20 rounded-2xl text-white font-bold"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!selected?.id) {
                      alert("Error: Employee ID missing!");
                      return;
                    }

                    try {
                      const cleanStructure = {
                        basic: Number(editSalaryStructure.basic) || 0,
                        hra: Number(editSalaryStructure.hra) || 0,
                        da: Number(editSalaryStructure.da) || 0,
                        cca: Number(editSalaryStructure.cca) || 0,
                        specialAllowance: Number(editSalaryStructure.specialAllowance) || 0,
                        performancePay: Number(editSalaryStructure.performancePay) || 0,
                        meal: Number(editSalaryStructure.meal) || 0,
                        gross: Number(editSalaryStructure.gross) || 0,
                      };

                      await updateDoc(doc(db, "employees", selected.email), {
                        salaryStructure: cleanStructure,
                        updatedAt: serverTimestamp(),
                      });

                      alert(`Salary updated for ${selected.name}!`);
                      setShowSalaryModal(false);
                    } catch (err) {
                      console.error("Update failed:", err);
                      alert("Failed: " + err.message);
                    }
                  }}
                >
                  Save Changes
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* FOOTER */}
      <div className="text-center mt-6 text-white/40 text-sm">
        <p>Powered by <strong className="text-cyan-300">DriveEasy HR</strong> • Secure • Real-time</p>
      </div>
    </div>
  );
};

export default HrPayouts;